/**
 * Realtime solve-completion waiting over the schedule store. This helper subscribes and times out;
 * callers own any fallback status polling.
 *
 * @module apps/web/src/lib/solveCompletion
 */

import { useScheduleStore, type SolveStatus } from '@/stores/scheduleStore';

/**
 * Recognize completed, blocked, cancelled, and failed solve states as terminal.
 */
export const terminalSolveStatus = (status?: string) =>
  ['completed', 'blocked', 'cancelled', 'failed'].includes(status || '');

/**
 * Resolve an already-terminal or subsequently terminal solve status matching jobId,
 * or null after timeoutMs (default 2000). Removes the subscription on completion.
 * Cancellation resolves null and releases its subscription immediately.
 * Callers own any fallback status polling; this helper only watches store updates.
 */
export function waitForSolveCompletion(
  jobId?: string,
  timeoutMs = 2000,
  signal?: AbortSignal
): Promise<SolveStatus | null> {
  return new Promise(resolve => {
    if (signal?.aborted) {
      resolve(null);
      return;
    }
    const match = () => {
      const status = useScheduleStore.getState().solveStatus;
      return status && status.job_id === jobId && terminalSolveStatus(status.status)
        ? status
        : null;
    };
    const current = match();
    if (current) {
      resolve(current);
      return;
    }
    const finish = (status: SolveStatus | null) => {
      clearTimeout(timer);
      unsubscribe();
      signal?.removeEventListener('abort', aborted);
      resolve(status);
    };
    const aborted = () => finish(null);
    const timer = setTimeout(() => finish(null), timeoutMs);
    const unsubscribe = useScheduleStore.subscribe(() => {
      const status = match();
      if (status) {
        finish(status);
      }
    });
    signal?.addEventListener('abort', aborted, { once: true });
  });
}
